'use client';

import React, { useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  CardContent,
  Stack,
  alpha,
  CircularProgress,
} from '@mui/material';
import { AccountBalanceWallet, TrendingUp, Security, Speed } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { LayoutContainer } from '../../kit';
import WalletSection from '../payment/WalletSection';
import ProtectedRoute from '../protectedRoute';
import useWallet from '../payment/useWallet';
import usePaymentActions from '../payment/usePaymentActions';
import useUpiIntentActions from '../payment/useUpiIntentActions';
import { useRouter } from 'next/navigation';
import { themeColors } from '../../theme/theme';
import { useTranslation } from '../../kit/useTranslation';

const USE_UPI_INTENT = process.env.NEXT_PUBLIC_USE_UPI_INTENT === 'true';

// Feature card for desktop sidebar
const FeatureCard = ({ icon: Icon, title, description }) => (
  <Card
    elevation={0}
    sx={{
      bgcolor: alpha(themeColors.primaryColor, 0.05),
      borderRadius: 3,
      border: `1px solid ${alpha(themeColors.primaryColor, 0.1)}`,
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            bgcolor: alpha(themeColors.primaryColor, 0.1),
            color: themeColors.primaryColor,
          }}
        >
          <Icon sx={{ fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.95rem',
              color: '#1f2937',
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.5 }}>
            {description}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

function WalletContent() {
  const { t } = useTranslation();
  const userProfile = useSelector((state) => state.userProfile?.data);
  const user = userProfile?.user;
  const userId = user?.userId;
  const userPhone = user?.userPhoneNumber;
  const router = useRouter();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // Call all hooks first (before any conditional returns)
  const { wallet, loading: walletLoading, fetchWallet } = useWallet(userId);

  const { status, error, loading, pollPaymentStatus, handleCheckout } = usePaymentActions({
    userId,
    userPhone,
    bookingDetails: null,
    paymentAmount: 0,
    onWalletUpdate: fetchWallet,
    router,
  });

  // UPI Intent actions (hook is always called but only used when flag is on)
  const upiIntent = useUpiIntentActions({
    userId,
    userPhone,
    onWalletUpdate: fetchWallet,
  });

  // Use ref to prevent multiple polls for the same orderId (declared unconditionally)
  const polledOrderIdRef = useRef(null);

  // GUARD: Redirect if no valid user data
  useEffect(() => {
    if (!user || !userId) {
      router.replace('/login');
    }
  }, [user, userId, router]);

  useEffect(() => {
    // Don't run polling when there's no authenticated user
    if (!user) return;

    // Poll payment status after redirect from payment gateway
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('orderId');

    // Only poll if we have an orderId and haven't already polled for it
    if (orderId && polledOrderIdRef.current !== orderId) {
      polledOrderIdRef.current = orderId;
      pollPaymentStatus(orderId).then((success) => {
        if (success) {
          // Force wallet fetch after successful payment
          fetchWallet();
        }
      });
    }
  }, [pollPaymentStatus, fetchWallet, user]);

  // Redirect to login if no user data (happens on logout)
  useEffect(() => {
    if (!user && !userProfile) {
      router.push('/login');
    }
  }, [user, userProfile, router]);

  // Early return with loading if no user (AFTER all hooks)
  if (!user || !userId) {
    return (
      <LayoutContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </LayoutContainer>
    );
  }

  const features = [
    {
      icon: Speed,
      title: t('wallet.instantPayments'),
      description: t('wallet.instantPaymentsDesc'),
    },
    {
      icon: Security,
      title: t('wallet.secureAndSafe'),
      description: t('wallet.secureAndSafeDesc'),
    },
    {
      icon: TrendingUp,
      title: t('wallet.trackSpending'),
      description: t('wallet.trackSpendingDesc'),
    },
  ];

  return (
    <LayoutContainer>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#fafafa',
          pb: { xs: 10, md: 4 },
          pt: { xs: 0, md: 3 },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            px: { xs: 2, md: 4 },
          }}
        >
          {/* Page Header */}
          {isDesktop && (
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(themeColors.primaryColor, 0.1),
                  color: themeColors.primaryColor,
                }}
              >
                <AccountBalanceWallet sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#1f2937',
                  }}
                >
                  {t('wallet.title')}
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '0.9rem' }}>
                  {t('wallet.subtitle')}
                </Typography>
              </Box>
            </Stack>
          )}

          {/* Desktop: Two-column layout */}
          {isDesktop ? (
            <Grid container spacing={4}>
              {/* Main Content - Wallet */}
              <Grid item xs={12} md={isLargeDesktop ? 7 : 8}>
                <WalletSection
                  wallet={wallet}
                  onAddMoney={handleCheckout}
                  onRefresh={fetchWallet}
                  loading={loading}
                  error={error}
                  status={status}
                  upiIntent={USE_UPI_INTENT ? upiIntent : undefined}
                />
              </Grid>

              {/* Sidebar - Features */}
              <Grid item xs={12} md={isLargeDesktop ? 5 : 4}>
                <Box sx={{ position: 'sticky', top: 24 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      color: '#1f2937',
                      mb: 2,
                    }}
                  >
                    {t('wallet.whyUseWallet')}
                  </Typography>
                  <Stack spacing={2}>
                    {features.map((feature, index) => (
                      <FeatureCard key={index} {...feature} />
                    ))}
                  </Stack>

                  {/* Quick stats card */}
                  <Card
                    elevation={0}
                    sx={{
                      mt: 3,
                      borderRadius: 3,
                      border: '1px solid #e5e7eb',
                      bgcolor: '#fff',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: '#6b7280',
                          fontSize: '0.8rem',
                          mb: 2,
                        }}
                      >
                        {t('wallet.walletSummary')}
                      </Typography>
                      <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ color: '#6b7280', fontSize: '0.9rem' }}>
                            {t('wallet.availableBalance')}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#059669',
                              fontSize: '0.95rem',
                            }}
                          >
                            ₹{wallet?.available || 0}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ color: '#6b7280', fontSize: '0.9rem' }}>
                            {t('wallet.blockedAmount')}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: '#f59e0b',
                              fontSize: '0.95rem',
                            }}
                          >
                            ₹{wallet?.blocked || 0}
                          </Typography>
                        </Stack>
                        <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2, mt: 1 }}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: '#374151',
                                fontSize: '0.9rem',
                              }}
                            >
                              {t('wallet.totalBalance')}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: '#1f2937',
                                fontSize: '1rem',
                              }}
                            >
                              ₹
                              {(
                                (Number(wallet?.available) || 0) + (Number(wallet?.blocked) || 0)
                              ).toFixed(2)}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          ) : (
            /* Mobile: Single column */
            <WalletSection
              wallet={wallet}
              onAddMoney={handleCheckout}
              onRefresh={fetchWallet}
              loading={loading}
              error={error}
              status={status}
              upiIntent={USE_UPI_INTENT ? upiIntent : undefined}
            />
          )}
        </Container>
      </Box>
    </LayoutContainer>
  );
}

export default function WalletPage() {
  return (
    <ProtectedRoute>
      <WalletContent />
    </ProtectedRoute>
  );
}
